<template>
  <div>
    <DevicesActivityTab
      :class="[$style.card, {
        [$style.activeCard]: isVisibleCard(0),
      }]"
      :daysFilter="daysFilter"
    />
    <Card
      v-for="(item, index) in cardsData"
      :key="item.name"
      :class="[$style.card, {
        [$style.activeCard]: isVisibleCard(index + 1),
      }]"
      :data-test-id="`Card__${item.name}`"
    >
      <div :class="$style.devicesSummary">
        <div :class="$style.devicesSummaryItem">
          <HeadingLarge>
            {{ getPercentage(cardsData, index, 'sum') }}
          </HeadingLarge>
          <HeadingMedium weight="light">
            of fleet
          </HeadingMedium>
        </div>
        <div :class="$style.devicesSummaryItem">
          <HeadingLarge>
            {{ item.sum }}
          </HeadingLarge>
          <HeadingMedium weight="light">
            {{ getDevicesLabel(item) }}
          </HeadingMedium>
        </div>
        <div :class="$style.devicesSummaryItem">
          <HeadingLarge>
            {{ item.osVersionList.length }}
          </HeadingLarge>
          <HeadingMedium weight="light">
            OS {{ getVersionsLabel(item) }}
          </HeadingMedium>
        </div>
      </div>
      <TableWithEndlessScroll
        :class="$style.systemFleetTable"
        :columns="tableColumns(item)"
        :moreToLoad="false"
        :sortBy="sortBy"
        :tableData="tableData[index]"
        @cellClick="handleCellClick"
        @headerClick="column => handleHeaderClick(index, column)"
      />
    </Card>
  </div>
</template>

<script>
import { Card, HeadingLarge, HeadingMedium } from '@jumpcloud/ui-components';
import { DeviceSupportedOSLabels } from 'client/util/Constants';
import { cloneDeep } from 'lodash';
import { mapActions } from 'vuex';
import DevicesActivityTab from 'client/OsManagement/DonutChart/DevicesActivityTab';
import EndOfServiceLabel from 'client/OsManagement/OsReleasePatch/EndOfServiceLabel';
import ItemType from 'client/util/Constants/ItemType';
import OsManagementMixin from 'client/OsManagement/mixins/OsManagementMixin';
import ReleaseStatusLabel from 'client/OsManagement/OsReleasePatch/ReleaseStatusLabel';
import SystemFilters
  from 'client/components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import TableWithEndlessScroll from 'client/components/Tables/TableWithEndlessScroll';
import pluralize from 'pluralize';

const { windows } = DeviceSupportedOSLabels;
const { DeviceItemType } = ItemType;
const deviceItem = new DeviceItemType();
export default {
  name: 'ChartSummaryCards',

  components: {
    Card, DevicesActivityTab, HeadingLarge, HeadingMedium, TableWithEndlessScroll,
  },

  mixins: [OsManagementMixin],

  props: {
    cardsData: {
      type: Array,
      default: () => [],
    },
    daysFilter: {
      type: Number,
      default: 30,
    },
    visibleCard: {
      type: Number,
      default: 0,
    },
  },

  data: () => ({
    columns: [
      { dataField: 'name', name: 'Name', sortBy: 'name' },
      {
        dataField: 'version',
        name: 'Version',
        sortBy: 'version',
        clickable: () => true,
        bodyComponent: ReleaseStatusLabel,
      },
      { dataField: 'count', name: 'Device Count', sortBy: 'count' },
    ],
    columnsWindows: [
      { dataField: 'name', name: 'Name', sortBy: 'name' },
      {
        dataField: 'version',
        name: 'Build',
        sortBy: 'version',
        clickable: () => true,
        bodyComponent: ReleaseStatusLabel,
      },
      { dataField: 'count', name: 'Count', sortBy: 'count' },
    ],
    sortBy: '',
    tableData: [],
  }),

  computed: {
    isDeviceOverviewEndOfServiceActive() {
      return this.$hasFeatureFlag(this.$featureFlags.deviceOverviewEndOfService);
    },
  },

  watch: {
    cardsData() {
      this.tableData = this.getTableData(this.cardsData);
    },

    visibleCard() {
      this.resetAllSortByFields();
    },
  },

  created() {
    this.handleCreated();
  },

  methods: {
    ...mapActions('SystemCollection', [
      'setFilters',
    ]),

    getDevicesLabel(item) {
      return pluralize(deviceItem.label, item.sum);
    },

    getRowDataByVersion(version) {
      const index = this.visibleCard - 1;
      return this.tableData[index].find(release => release.version === String(version, ''));
    },

    getTableData(cardsData) {
      return cardsData.map(os => (
        os.osVersionList.map(version => ({
          ...this.formatVersionName(os.name, version.name),
          count: version.sum,
          fullVersion: version.name,
        })).sort((aSortElement, bSortElement) => {
          if (os.name.includes(windows)) {
            return (aSortElement.version - bSortElement.version);
          }
          return (aSortElement.version > bSortElement.version) ? 1 : -1;
        }).reverse()
      ));
    },

    getVersionsLabel(item) {
      return pluralize('version', item.osVersionList.length);
    },

    handleCellClick(record, value) {
      const { name, version } = this.getRowDataByVersion(value);

      const filters = [];
      if (name.includes('macOS')) {
        const osVersion = version.split(' ');
        filters.push(SystemFilters.osName('macOS'));
        filters.push(SystemFilters.osRelease(name.replace('macOS ', '')));
        // Version only supports tenths Example: 11.0 or 11.4
        filters.push(SystemFilters.osVersion(osVersion[0].slice(0, -2)));
        filters.push(SystemFilters.releaseRevision(osVersion[1].replace('(', '').replace(')', '')));
      } else if (name.includes('Windows')) {
        filters.push(SystemFilters.osName('Windows'));
        filters.push(SystemFilters.releasePatch(version.split('.')[0], 'Windows'));
        filters.push(SystemFilters.releaseRevision(version.split('.')[1]));
      } else {
        // Linux and others
        const osVersion = version.includes('(') ? version.split('(') : version;

        filters.push(SystemFilters.distributionName(name));

        // If version includes release name add it to filters
        if (version.includes('(')) {
          filters.push(SystemFilters.osRelease(osVersion[1].slice(0, -1)));

          filters.push(SystemFilters.osMajor(osVersion[0].split('.')[0]));
          filters.push(SystemFilters.osMinor(osVersion[0].split('.')[1]));
          filters.push(SystemFilters.releasePatch(osVersion[0].split('.')[2][0]));
        } else {
          filters.push(SystemFilters.osMajor(osVersion.split('.')[0]));
          filters.push(SystemFilters.osMinor(osVersion.split('.')[1]));
          filters.push(SystemFilters.releasePatch(osVersion.split('.')[2][0]));
        }
      }

      // Set Filters
      this.setFilters({ filtersArray: filters });

      // Redirect route
      this.$router.push({ name: 'devicesList' });
    },

    handleCreated() {
      this.tableData = this.getTableData(this.cardsData);

      if (this.isDeviceOverviewEndOfServiceActive) {
        this.insertWindowsNewColumn();
      }
    },

    handleHeaderClick(index, column) {
      let isWindows = false;
      let columnsToSort = this.columns;

      // Check if the first element is Windows system to use the columns for Windows
      if (this.tableData[index][0].name.includes(windows)) {
        isWindows = true;
        columnsToSort = this.columnsWindows;
      }

      const { sortBy, dataField } = column;
      this.sortBy = dataField;
      const descending = sortBy.includes('-');

      this.setSortByFields(columnsToSort, column, descending, isWindows);

      if (isWindows) {
        this.sortWindowsTable(index, dataField, descending);
      } else {
        this.sortTable(index, dataField, descending);
      }
    },

    insertWindowsNewColumn() {
      this.columnsWindows.splice(2, 0, {
        bodyComponent: EndOfServiceLabel,
        dataField: 'endOfService',
        name: 'End Of Service',
        sortBy: 'endOfService',
        hasTooltip: true,
        orientation: 'left',
        tooltipText: 'The end-of-support date listed in the table refers to Home and Pro editions. The date for other editions, such as Education and Enterprise, may differ.',
      });
    },

    isVisibleCard(index) {
      return this.visibleCard === index;
    },

    resetAllSortByFields() {
      this.columns = this.columns.map(col => ({
        ...col,
        sortBy: col.dataField,
      }));

      this.sortBy = '';
    },

    setSortByFields(columnsRef, column, descending, isWindows) {
      // Adds or removes the dash prefix for descending so that next time it's the proper sort type
      const columnsSorted = columnsRef.map(col => {
        const newColumn = cloneDeep(col);
        if (col.dataField === column.dataField) {
          newColumn.sortBy = descending ? col.dataField : `-${col.sortBy}`;
        } else {
          // Set all other columns' sort to ascending as default
          newColumn.sortBy = col.dataField;
        }

        return newColumn;
      });

      if (isWindows) {
        this.columnsWindows = columnsSorted;
      }
      this.columns = columnsSorted;
    },

    sortTable(index, dataField, descending) {
      this.tableData[index].sort((a, b) => {
        let aSortField = a[dataField];
        let bSortField = b[dataField];

        // Convert to string if it's name or version to prevent issues on sorting
        if (dataField === 'name' || dataField === 'version') {
          aSortField = String(a[dataField] || '').toLowerCase();
          bSortField = String(b[dataField] || '').toLowerCase();
        }

        // Ascending
        if (aSortField < bSortField) {
          return -1;
        }
        if (aSortField > bSortField) {
          return 1;
        }
        return 0;
      });

      if (!descending) {
        this.tableData[index].reverse();
      }
    },

    sortWindowsTable(index, dataField, descending) {
      this.tableData[index].sort((a, b) => {
        if (dataField === 'version') {
          return (a.version - b.version);
        }

        if (dataField === 'name') {
          return a.name.localeCompare(b.name, 'en-US', { ignorePunctuation: true, sensitivity: 'accent', numeric: true });
        }

        // Ascending
        if (a.count < b.count) {
          return -1;
        }
        if (a.count > b.count) {
          return 1;
        }
        return 0;
      });

      if (!descending) {
        this.tableData[index].reverse();
      }
    },

    tableColumns(item) {
      let { columns } = this;

      if (item.name.includes(windows)) {
        columns = this.columnsWindows;
      }

      return columns;
    },
  },
};
</script>
<style module>
@import './FleetDistributionCommon.css';

@value (
  jcGrey600,
  jcGrey800,
  jcGrey) from '~css/brand-colors.css';

.card {
  border: none !important;
  display: none;
  padding: 2rem 1.25rem !important;
  width: 100%;
}

.activeCard {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.devicesSummary {
  align-items: baseline;
  display: flex;
  flex: 3;
}

.devicesSummaryItem {
  align-items: baseline;
  display: flex;
  gap: var(--jc-spacer-small);
  margin-right: 1.5rem;
}

</style>
